import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Upload, Image, GripVertical, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// The password is checked by the server, which hands back a login token that
// expires. Every change is sent with that token.
const TOKEN_KEY = 'pib_admin_token';

const getToken = () => {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const { token, expires } = JSON.parse(raw);
    return expires * 1000 > Date.now() ? token : null;
  } catch {
    return null;
  }
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const AdminPortfolio = () => {
  const { toast } = useToast();

  // This page has no SEO tags of its own; give the browser tab a name.
  useEffect(() => {
    document.title = 'Admin | Paint It Better';
  }, []);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Interior Painting',
    image: '',
    description: '',
    location: '',
    images: []  // Gallery images
  });
  const [imagePreview, setImagePreview] = useState('');
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  const categories = ['Interior Painting', 'Exterior Painting', 'Wallpapering'];

  // Check if already authenticated (session storage)
  useEffect(() => {
    if (getToken()) {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPortfolio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    let session = null;
    try {
      const response = await axios.post(`${API}/admin/login`, { password: passwordInput });
      session = response.data;
    } catch (error) {
      session = null;
    }
    if (session && session.token) {
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify(session));
      setIsAuthenticated(true);
      setPasswordInput('');
      setLoginError('');
      toast({
        title: "Welcome!",
        description: "You're now logged in to the admin panel.",
      });
    } else {
      setLoginError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem(TOKEN_KEY);
    setPasswordInput('');
  };

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setPortfolioItems(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch portfolio items",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCoverImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData(prev => ({ ...prev, image: url }));
    setImagePreview(url);
  };

  const addGalleryImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (url && url.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url.trim()]
      }));
      setGalleryPreviews(prev => [...prev, url.trim()]);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'Interior Painting',
      image: '',
      description: '',
      location: '',
      images: []
    });
    setImagePreview('');
    setGalleryPreviews([]);
    setEditingItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const portfolioData = {
        title: formData.title,
        category: formData.category,
        image: formData.image || '/images/portfolio/project_01.jpg',
        description: formData.description,
        location: formData.location.trim(),
        images: formData.images
      };

      if (editingItem) {
        await axios.put(`${API}/portfolio/${editingItem.id}`, portfolioData, authHeaders());
        toast({
          title: "Success",
          description: "Portfolio item updated successfully"
        });
      } else {
        await axios.post(`${API}/portfolio`, portfolioData, authHeaders());
        toast({
          title: "Success",
          description: "Portfolio item added successfully"
        });
      }

      fetchPortfolio();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      if (error.response && error.response.status === 401) handleLogout();
      toast({
        title: "Error",
        description: "Failed to save portfolio item",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      category: item.category,
      image: item.image,
      description: item.description,
      location: item.location || '',
      images: item.images || []
    });
    setImagePreview(item.image);
    setGalleryPreviews(item.images || []);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this portfolio item?')) {
      return;
    }

    try {
      await axios.delete(`${API}/portfolio/${id}`, authHeaders());
      toast({
        title: "Success",
        description: "Portfolio item deleted successfully"
      });
      fetchPortfolio();
    } catch (error) {
      if (error.response && error.response.status === 401) handleLogout();
      toast({
        title: "Error",
        description: "Failed to delete portfolio item",
        variant: "destructive"
      });
    }
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-8">
        <div className="w-full max-w-md px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-100 rounded-full mb-4">
                <Lock className="h-8 w-8 text-cyan-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Admin Login</h1>
              <p className="text-slate-600 mt-2">Enter your password to access the portfolio manager</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter admin password"
                    className="pr-10"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {loginError && (
                <p className="text-red-500 text-sm">{loginError}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="admin-login-button"
              >
                Login
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Portfolio Management</h1>
            <p className="text-slate-600 mt-2">Add, edit, or remove portfolio items</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="text-slate-600"
            >
              Logout
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              <Plus className="mr-2 h-5 w-5" />
              Add New Project
            </Button>
          </div>
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioItems.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <div className="relative h-48 bg-slate-200">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                {/* Gallery count badge */}
                {item.images && item.images.length > 0 && (
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                    <Image className="h-3 w-3" />
                    {item.images.length + 1} photos
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleEdit(item)}
                    className="bg-white text-slate-900 hover:bg-slate-100"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    variant="destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="mb-2">
                  <span className="inline-block bg-cyan-100 text-cyan-800 text-xs px-2 py-1 rounded">
                    {item.category}
                  </span>
                </div>
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 line-clamp-2">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">
                    {editingItem ? 'Edit Project' : 'Add New Project'}
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Title */}
                  <div>
                    <Label htmlFor="title">Project Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Modern Kitchen Transformation"
                      className="mt-2"
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <Label htmlFor="location">Town</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g., Canterbury (town only, no street or postcode)"
                      className="mt-2"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Cover Image URL */}
                  <div>
                    <Label htmlFor="image">Cover Image URL *</Label>
                    <Input
                      id="image"
                      type="url"
                      value={formData.image}
                      onChange={handleCoverImageUrlChange}
                      placeholder="e.g., /images/portfolio/project_01.jpg"
                      className="mt-2"
                      required
                    />
                    {imagePreview && (
                      <div className="mt-3">
                        <img
                          src={imagePreview}
                          alt="Cover Preview"
                          className="w-full h-40 object-cover rounded-lg border"
                        />
                      </div>
                    )}
                  </div>

                  {/* Gallery Images */}
                  <div>
                    <Label>Gallery Images (Additional Photos)</Label>
                    <p className="text-sm text-slate-500 mb-3">Add extra images to show more of this project</p>
                    
                    {/* Gallery Thumbnails */}
                    {galleryPreviews.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {galleryPreviews.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img
                              src={img}
                              alt={`Gallery ${idx + 1}`}
                              className="w-full h-20 object-cover rounded-md border"
                            />
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(idx)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addGalleryImageUrl}
                      className="w-full border-dashed"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Gallery Image URL
                    </Button>
                  </div>

                  {/* Description */}
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      placeholder="Brief description of the project"
                      className="mt-2"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      {editingItem ? 'Update Project' : 'Add Project'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPortfolio;
